import * as Accordion from '@radix-ui/react-accordion';
import * as Select from '@radix-ui/react-select';
import type { KeyboardEvent, MouseEvent, PointerEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { Icon } from '~/components';
import { DropdownItemLabel } from '~/components/DropdownTreeSingleSelect/components/DropdownItemLabel';
import { DropdownItemTrigger } from '~/components/DropdownTreeSingleSelect/components/DropdownItemTrigger';
import { ACCORDION_TOGGLE_ANIMATION_MS } from '~/components/DropdownTreeSingleSelect/constants';
import type {
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/components/DropdownTreeSingleSelect/types';
import { cn } from '~/lib/cn';

interface DropdownItemProps extends RadixUiProps {
  depth: number;
  option: InternalDropdownOption;
  selectedOption?: InternalDropdownOption;
  onSelectOption?: (option: InternalDropdownOption) => void;
  showCheckIcon?: boolean;
  searchQuery?: string;
  OptionTemplate?: OptionTemplateComponent;
}

export function DropdownItem({
  depth,
  option,
  selectedOption,
  onSelectOption,
  showCheckIcon,
  searchQuery,
  OptionTemplate,
  radix = {},
}: DropdownItemProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selectItemRef = useRef<HTMLDivElement>(null);
  const shouldRetainFocusRef = useRef(false);
  const isUnselectableParent = option.unselectable && !!option.children?.length;
  const hasChildren = !!option.children?.length;
  const hasVisibleChildren =
    option.children?.some((childOption) => childOption.isVisible) ?? false;

  // Toggle the accordion open/closed to reflect the isExpanded state.
  useEffect(() => {
    if (!option.isExpanded && triggerRef.current?.dataset.state === 'open') {
      triggerRef.current.click();
    }

    if (option.isExpanded && triggerRef.current?.dataset.state === 'closed') {
      triggerRef.current.click();
    }
  }, [option.isExpanded]);

  /**
   * Radix Select refocuses the selected item when the item collection changes.
   * After a user-initiated expand/collapse, restore focus to this parent item.
   *
   * Expanding: immediate focus is enough. Collapsing: Collapsible unmounts child
   * Select.Items after the slide-up animation, which triggers Radix to refocus
   * the selected child and focus is lost — so we refocus after that settles
   * (see ACCORDION_TOGGLE_ANIMATION_MS).
   */
  useEffect(() => {
    if (!shouldRetainFocusRef.current || !option.hasSelected) {
      return;
    }

    shouldRetainFocusRef.current = false;

    const restoreFocus = () => {
      selectItemRef.current?.focus();
    };

    if (option.isExpanded) {
      restoreFocus();

      return;
    }

    const timeoutId = window.setTimeout(
      restoreFocus,
      ACCORDION_TOGGLE_ANIMATION_MS,
    );

    const activeElement = document.activeElement;

    activeElement?.addEventListener('blur', restoreFocus, { once: true });

    return () => {
      window.clearTimeout(timeoutId);
      activeElement?.removeEventListener('blur', restoreFocus);
    };
  }, [option.isExpanded, option.hasSelected]);

  const markFocusForRetention = useCallback(() => {
    shouldRetainFocusRef.current = true;
  }, []);

  const handleOptionInteraction = useCallback(() => {
    if (isUnselectableParent) {
      markFocusForRetention();
      triggerRef.current?.click();

      return;
    }

    onSelectOption?.(option);
  }, [isUnselectableParent, markFocusForRetention, onSelectOption, option]);

  const handleOptionMouseUp = useCallback(() => {
    handleOptionInteraction();
  }, [handleOptionInteraction]);

  const handleOptionKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (['Enter', ' '].includes(event.key)) {
        handleOptionInteraction();
      }
    },
    [handleOptionInteraction],
  );

  const handleUnselectableParentPointerUp = useCallback(
    (event: PointerEvent) => {
      // Prevent Radix Select from treating this item as selected.
      event.preventDefault();
      handleOptionInteraction();
    },
    [handleOptionInteraction],
  );

  const handleUnselectableParentClick = useCallback((event: MouseEvent) => {
    event.preventDefault();
  }, []);

  const handleExpandChildren = useCallback(() => {
    if (!hasVisibleChildren || option.isExpanded) {
      return;
    }

    markFocusForRetention();
    triggerRef.current?.click();
  }, [hasVisibleChildren, markFocusForRetention, option.isExpanded]);

  const handleCollapseChildren = useCallback(() => {
    if (!hasChildren || !option.isExpanded) {
      return;
    }

    markFocusForRetention();
    triggerRef.current?.click();
  }, [hasChildren, markFocusForRetention, option.isExpanded]);

  const handleSelectItemKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (hasChildren) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          handleExpandChildren();

          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          handleCollapseChildren();

          return;
        }
      }

      if (isUnselectableParent && ['Enter', ' '].includes(event.key)) {
        // Prevent Radix Select from closing and updating the value.
        event.preventDefault();
        handleOptionInteraction();
      }
    },
    [
      hasChildren,
      handleExpandChildren,
      handleCollapseChildren,
      isUnselectableParent,
      handleOptionInteraction,
    ],
  );

  let OptionLabel = OptionTemplate || DropdownItemLabel;

  const itemClassName = cn(
    'flex cursor-pointer gap-xs px-md py-sm transition-colors duration-300 text-default',
    'hover:bg-emphasis-strong focus:outline-none focus:bg-emphasis-strong',
    {
      'select-none': isUnselectableParent,
      'focus:bg-secondary-strong bg-primary-default hover:bg-secondary-default':
        !isUnselectableParent && selectedOption?.value === option.value,
    },
    radix.selectItem?.className,
  );

  const itemStyle = {
    paddingLeft: depth ? `calc(var(--spacing-md) * ${depth + 1})` : undefined,
    ...radix.selectItem?.style,
  };

  const itemContent = (
    <>
      {showCheckIcon ? (
        <div className="-ml-xs w-md">
          {!isUnselectableParent ? (
            <Select.ItemIndicator>
              <Icon type="check" size="sm" />
            </Select.ItemIndicator>
          ) : null}
        </div>
      ) : null}
      <Select.ItemText>
        <OptionLabel option={option} searchQuery={searchQuery} />
      </Select.ItemText>
    </>
  );

  return (
    <Accordion.Header
      {...radix.accordionHeader}
      className={cn('relative', radix.accordionHeader?.className)}
    >
      {option.children?.length ? (
        <div className="absolute inset-y-0 end-0 flex items-center">
          <DropdownItemTrigger
            triggerRef={triggerRef}
            option={option}
            onBeforeToggle={markFocusForRetention}
          />
        </div>
      ) : null}
      <Select.Item
        {...radix.selectItem}
        value={option.value}
        className={itemClassName}
        style={itemStyle}
        onMouseUp={isUnselectableParent ? undefined : handleOptionMouseUp}
        onKeyUp={isUnselectableParent ? undefined : handleOptionKeyUp}
        onPointerUp={
          isUnselectableParent ? handleUnselectableParentPointerUp : undefined
        }
        onClick={
          isUnselectableParent ? handleUnselectableParentClick : undefined
        }
        onKeyDown={handleSelectItemKeyDown}
        asChild
        aria-selected={
          !isUnselectableParent && selectedOption?.value === option.value
        }
        data-item-depth={depth}
      >
        <div ref={selectItemRef}>{itemContent}</div>
      </Select.Item>
    </Accordion.Header>
  );
}

DropdownItem.displayName = 'DropdownItem';
